import { DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import {
  collectionGroup,
  Firestore,
  getDocs,
  limit,
  orderBy,
  query,
  where,
} from '@angular/fire/firestore';
import { TranslateModule } from '@ngx-translate/core';
import {
  AuthStore,
  DocumentRequest,
  ExpenseClaim,
  LeaveRequest,
  Timesheet,
  TodoItem,
  TodoStatus,
} from '../../../core';
import { AttendanceService } from '../../../core/services/attendance.service';
import { DocumentService } from '../../../core/services/document.service';
import { ExpenseService } from '../../../core/services/expense.service';
import { LeaveService } from '../../../core/services/leave.service';
import { TodoService } from '../../../core/services/todo.service';
import { UserDirectoryService } from '../../../core/services/user-directory.service';
import { LoadingSpinnerComponent } from '../loading-spinner/loading-spinner.component';

const BOARD_COLUMNS = [
  { key: 'open', labelKey: 'dashboard.kanban.columns.open', emptyKey: 'dashboard.kanban.empty.open' },
  {
    key: 'in_progress',
    labelKey: 'dashboard.kanban.columns.inProgress',
    emptyKey: 'dashboard.kanban.empty.inProgress',
  },
  {
    key: 'in_review',
    labelKey: 'dashboard.kanban.columns.inReview',
    emptyKey: 'dashboard.kanban.empty.inReview',
  },
  { key: 'done', labelKey: 'dashboard.kanban.columns.done', emptyKey: 'dashboard.kanban.empty.done' },
] as const;

type BoardStatus = (typeof BOARD_COLUMNS)[number]['key'];

type BoardItemType =
  | 'todo'
  | 'leave'
  | 'expense'
  | 'timesheet'
  | 'document_request'
  | 'onboarding_task';

interface BoardItem {
  id: string;
  type: BoardItemType;
  title: string;
  subtitle: string;
  status: BoardStatus;
  createdAt: Date;
  link: string | null;
}

@Component({
  selector: 'app-kanban-board',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterLink, TranslateModule, DatePipe, LoadingSpinnerComponent],
  template: `
    <section class="section section-kanban" aria-labelledby="kanban-title">
      <header class="section-header">
        <div>
          <h2 id="kanban-title" class="section-title">{{ 'dashboard.kanban.title' | translate }}</h2>
          <p class="section-subtitle">{{ 'dashboard.kanban.subtitle' | translate }}</p>
        </div>
      </header>

      <form class="todo-form" (ngSubmit)="addTodo()">
        <label class="todo-label" for="todo-title">{{ 'dashboard.kanban.addLabel' | translate }}</label>
        <div class="todo-input-row">
          <input
            id="todo-title"
            type="text"
            class="todo-input"
            [placeholder]="'dashboard.kanban.addPlaceholder' | translate"
            [value]="newTodoTitle()"
            (input)="onTodoInput($event)"
            [disabled]="savingTodo()" />
          <button type="submit" class="btn btn-primary" [disabled]="savingTodo() || !newTodoTitle()">
            {{ 'dashboard.kanban.addAction' | translate }}
          </button>
        </div>
      </form>

      @if (loading()) {
        <div class="kanban-loading">
          <app-loading-spinner size="md" />
        </div>
      } @else {
        <div class="kanban-grid">
          @for (column of columns(); track column.key) {
            <section class="kanban-column" [attr.aria-label]="column.labelKey | translate">
              <header class="kanban-column-header">
                <h3 class="kanban-column-title">{{ column.labelKey | translate }}</h3>
                <span class="kanban-count">{{ column.items.length }}</span>
              </header>

              @if (column.items.length === 0) {
                <p class="kanban-empty">{{ column.emptyKey | translate }}</p>
              } @else {
                <div class="kanban-list">
                  @for (item of column.items; track item.id) {
                    <article class="kanban-card" [class.todo-card]="item.type === 'todo'">
                      <header class="kanban-card-header">
                        <span class="kanban-type">{{ typeLabel(item.type) | translate }}</span>
                        <span class="kanban-date">{{ item.createdAt | date: 'MMM d' }}</span>
                      </header>
                      <h4 class="kanban-title">{{ item.title }}</h4>
                      <p class="kanban-subtitle">{{ item.subtitle }}</p>

                      @if (item.type === 'todo') {
                        <label class="todo-status-label" for="todo-status-{{ item.id }}">
                          {{ 'dashboard.kanban.statusLabel' | translate }}
                        </label>
                        <select
                          id="todo-status-{{ item.id }}"
                          class="todo-status"
                          [value]="todoStatus(item.id)"
                          (change)="onTodoStatusChange(item.id, $event)">
                          @for (option of todoStatusOptions; track option.value) {
                            <option [value]="option.value">{{ option.labelKey | translate }}</option>
                          }
                        </select>
                      } @else if (item.link) {
                        <a class="kanban-link" [routerLink]="item.link">
                          {{ 'dashboard.kanban.viewDetails' | translate }}
                        </a>
                      }
                    </article>
                  }
                </div>
              }
            </section>
          }
        </div>
      }
    </section>
  `,
  styles: `
    .section-kanban {
      margin-top: 2rem;
    }

    .todo-form {
      margin: 1rem 0 1.5rem;
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
    }

    .todo-label {
      font-size: 0.875rem;
      font-weight: 600;
      color: oklch(0.3 0 0);
    }

    .todo-input-row {
      display: flex;
      gap: 0.75rem;
      flex-wrap: wrap;
    }

    .todo-input {
      flex: 1 1 16rem;
      min-width: 12rem;
      padding: 0.65rem 0.75rem;
      border-radius: 0.5rem;
      border: 1px solid oklch(0.9 0 0);
      background: white;
      font-size: 0.875rem;
    }

    .kanban-loading {
      display: flex;
      justify-content: center;
      padding: 2rem 0;
    }

    .kanban-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
      gap: 1rem;
    }

    .kanban-column {
      background: oklch(0.98 0 0);
      border: 1px solid oklch(0.92 0 0);
      border-radius: 0.75rem;
      padding: 0.75rem;
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
      min-height: 12rem;
    }

    .kanban-column-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
    }

    .kanban-column-title {
      font-size: 0.9rem;
      font-weight: 600;
      color: oklch(0.25 0 0);
      margin: 0;
    }

    .kanban-count {
      font-size: 0.75rem;
      padding: 0.2rem 0.5rem;
      border-radius: 999px;
      background: oklch(0.92 0 0);
      color: oklch(0.35 0 0);
    }

    .kanban-empty {
      font-size: 0.8rem;
      color: oklch(0.55 0 0);
      margin: 0;
    }

    .kanban-list {
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
    }

    .kanban-card {
      background: white;
      border: 1px solid oklch(0.9 0 0);
      border-radius: 0.75rem;
      padding: 0.75rem;
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
    }

    .kanban-card.todo-card {
      border-style: dashed;
    }

    .kanban-card-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      font-size: 0.75rem;
      color: oklch(0.5 0 0);
    }

    .kanban-type {
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.04em;
    }

    .kanban-title {
      margin: 0;
      font-size: 0.95rem;
      font-weight: 600;
      color: oklch(0.25 0 0);
    }

    .kanban-subtitle {
      margin: 0;
      font-size: 0.85rem;
      color: oklch(0.5 0 0);
    }

    .kanban-link {
      margin-top: 0.25rem;
      font-size: 0.8rem;
      font-weight: 600;
      color: oklch(0.45 0.12 250);
      text-decoration: none;
    }

    .kanban-link:hover {
      text-decoration: underline;
    }

    .todo-status-label {
      font-size: 0.75rem;
      color: oklch(0.45 0 0);
    }

    .todo-status {
      padding: 0.4rem 0.5rem;
      border-radius: 0.5rem;
      border: 1px solid oklch(0.9 0 0);
      font-size: 0.8rem;
      background: white;
    }

    .btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      border: none;
      border-radius: 0.5rem;
      font-size: 0.85rem;
      font-weight: 600;
      padding: 0.6rem 1rem;
      cursor: pointer;
    }

    .btn-primary {
      background: oklch(0.45 0.12 250);
      color: white;
    }

    .btn-primary:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  `,
})
export class KanbanBoardComponent implements OnInit {
  private readonly authStore = inject(AuthStore);
  private readonly firestore = inject(Firestore);
  private readonly todoService = inject(TodoService);
  private readonly leaveService = inject(LeaveService);
  private readonly expenseService = inject(ExpenseService);
  private readonly attendanceService = inject(AttendanceService);
  private readonly documentService = inject(DocumentService);
  private readonly userDirectory = inject(UserDirectoryService);

  protected readonly newTodoTitle = signal('');
  protected readonly savingTodo = signal(false);

  private readonly docRequests = signal<DocumentRequest[]>([]);
  private readonly onboardingTasks = signal<
    Array<{ id: string; title: string; status: string; createdAt: Date }>
  >([]);
  private readonly loadingData = signal(false);

  protected readonly todoStatusOptions = [
    { value: 'open', labelKey: 'dashboard.kanban.columns.open' },
    { value: 'in_progress', labelKey: 'dashboard.kanban.columns.inProgress' },
    { value: 'in_review', labelKey: 'dashboard.kanban.columns.inReview' },
    { value: 'done', labelKey: 'dashboard.kanban.columns.done' },
  ] as const;

  protected readonly loading = computed(
    () =>
      this.loadingData() ||
      this.todoService.loading() ||
      this.leaveService.loading() ||
      this.expenseService.loading() ||
      this.attendanceService.loading()
  );

  protected readonly columns = computed(() => {
    const items = this.boardItems();
    return BOARD_COLUMNS.map((column) => ({
      ...column,
      items: this.sortItems(items.filter((item) => item.status === column.key)),
    }));
  });

  ngOnInit(): void {
    void this.loadBoardData();
  }

  protected async addTodo(): Promise<void> {
    if (this.savingTodo()) return;
    this.savingTodo.set(true);
    try {
      await this.todoService.createTodo(this.newTodoTitle());
      this.newTodoTitle.set('');
    } finally {
      this.savingTodo.set(false);
    }
  }

  protected onTodoInput(event: Event): void {
    const target = event.target as HTMLInputElement | null;
    this.newTodoTitle.set(target?.value ?? '');
  }

  protected onTodoStatusChange(todoId: string, event: Event): void {
    const target = event.target as HTMLSelectElement | null;
    const next = (target?.value ?? 'open') as TodoStatus;
    void this.todoService.updateStatus(todoId, next);
  }

  protected todoStatus(todoId: string): TodoStatus {
    return this.todoService.todos().find((item) => item.id === todoId)?.status ?? 'open';
  }

  protected typeLabel(type: BoardItemType): string {
    switch (type) {
      case 'todo':
        return 'dashboard.kanban.types.todo';
      case 'leave':
        return 'dashboard.kanban.types.leave';
      case 'expense':
        return 'dashboard.kanban.types.expense';
      case 'timesheet':
        return 'dashboard.kanban.types.timesheet';
      case 'document_request':
        return 'dashboard.kanban.types.document';
      case 'onboarding_task':
        return 'dashboard.kanban.types.onboarding';
    }
  }

  private async loadBoardData(): Promise<void> {
    this.loadingData.set(true);
    try {
      await this.userDirectory.loadUsers();
      await Promise.all([
        this.todoService.loadTodos(),
        this.loadApprovals(),
        this.loadDocumentRequests(),
        this.loadOnboardingTasks(),
      ]);
    } finally {
      this.loadingData.set(false);
    }
  }

  private async loadApprovals(): Promise<void> {
    const role = this.authStore.userRole();
    const tasks: Promise<void>[] = [];

    if (role === 'manager' || role === 'hr_admin') {
      tasks.push(this.leaveService.loadPendingApprovals());
      tasks.push(this.attendanceService.loadPendingApprovals());
    }

    if (role === 'manager' || role === 'hr_admin' || role === 'finance_admin') {
      tasks.push(this.expenseService.loadPendingApprovals());
    }

    if (tasks.length > 0) {
      await Promise.all(tasks);
    }
  }

  private async loadDocumentRequests(): Promise<void> {
    const requests = await this.documentService.listRequestsForBoard();
    this.docRequests.set(requests);
  }

  private async loadOnboardingTasks(): Promise<void> {
    const tenantId = this.authStore.tenantId();
    const userId = this.authStore.userId();
    if (!tenantId || !userId) {
      this.onboardingTasks.set([]);
      return;
    }

    const taskQuery = query(
      collectionGroup(this.firestore, 'tasks'),
      where('tenantId', '==', tenantId),
      where('assignee.userId', '==', userId),
      orderBy('createdAt', 'desc'),
      limit(200)
    );

    const snap = await getDocs(taskQuery);
    const tasks = snap.docs.map((docSnap) => {
      const data = docSnap.data() as Record<string, unknown>;
      return {
        id: docSnap.id,
        title: (data['title'] as string) ?? 'Onboarding task',
        status: (data['status'] as string) ?? 'todo',
        createdAt: this.parseTimestamp(data['createdAt']),
      };
    });

    this.onboardingTasks.set(tasks);
  }

  private boardItems(): BoardItem[] {
    const items: BoardItem[] = [];
    const role = this.authStore.userRole();

    for (const todo of this.todoService.todos()) {
      items.push(this.mapTodo(todo));
    }

    if (role === 'manager' || role === 'hr_admin') {
      for (const request of this.leaveService.approvals()) {
        items.push(this.mapLeave(request));
      }
    }

    if (role === 'manager' || role === 'hr_admin' || role === 'finance_admin') {
      for (const claim of this.expenseService.pendingApprovals()) {
        items.push(this.mapExpense(claim));
      }
    }

    if (role === 'manager' || role === 'hr_admin') {
      for (const sheet of this.attendanceService.pendingApprovals()) {
        items.push(this.mapTimesheet(sheet));
      }
    }

    for (const request of this.docRequests()) {
      items.push(this.mapDocumentRequest(request));
    }

    for (const task of this.onboardingTasks()) {
      items.push(this.mapOnboardingTask(task));
    }

    return items;
  }

  private mapTodo(todo: TodoItem): BoardItem {
    return {
      id: `todo_${todo.id}`,
      type: 'todo',
      title: todo.title,
      subtitle: this.authStore.user()?.displayName ?? '',
      status: todo.status,
      createdAt: todo.createdAt,
      link: null,
    };
  }

  private mapLeave(request: LeaveRequest): BoardItem {
    return {
      id: `leave_${request.id}`,
      type: 'leave',
      title: this.userDirectory.getDisplayName(request.userId),
      subtitle: `Leave · ${request.leaveType} · ${request.days}d`,
      status: 'in_review',
      createdAt: request.createdAt,
      link: '/approvals',
    };
  }

  private mapExpense(claim: ExpenseClaim): BoardItem {
    return {
      id: `expense_${claim.id}`,
      type: 'expense',
      title: this.userDirectory.getDisplayName(claim.userId),
      subtitle: `Expense · ${claim.amount} ${claim.currency}`,
      status: 'in_review',
      createdAt: claim.createdAt,
      link: '/expenses/manage',
    };
  }

  private mapTimesheet(sheet: Timesheet): BoardItem {
    return {
      id: `timesheet_${sheet.id}`,
      type: 'timesheet',
      title: this.userDirectory.getDisplayName(sheet.userId),
      subtitle: `Timesheet · ${sheet.weekStartDate.toDateString()}`,
      status: 'in_review',
      createdAt: sheet.createdAt,
      link: '/approvals',
    };
  }

  private mapDocumentRequest(request: DocumentRequest): BoardItem {
    const status = this.mapDocumentStatus(request.status);
    const owner = this.userDirectory.getDisplayName(request.targetUserId);
    return {
      id: `document_${request.id}`,
      type: 'document_request',
      title: owner,
      subtitle: `Document · ${request.title}`,
      status,
      createdAt: request.createdAt,
      link: '/documents/provided-requests',
    };
  }

  private mapOnboardingTask(task: { id: string; title: string; status: string; createdAt: Date }): BoardItem {
    return {
      id: `onboarding_${task.id}`,
      type: 'onboarding_task',
      title: task.title,
      subtitle: 'Onboarding task',
      status: this.mapOnboardingStatus(task.status),
      createdAt: task.createdAt,
      link: '/onboarding/team',
    };
  }

  private mapDocumentStatus(status: DocumentRequest['status']): BoardStatus {
    switch (status) {
      case 'requested':
        return 'open';
      case 'provided':
        return 'in_review';
      case 'fulfilled':
      case 'cancelled':
        return 'done';
    }
  }

  private mapOnboardingStatus(status: string): BoardStatus {
    switch (status) {
      case 'in_progress':
        return 'in_progress';
      case 'blocked':
        return 'in_review';
      case 'done':
      case 'skipped':
        return 'done';
      default:
        return 'open';
    }
  }

  private sortItems(items: BoardItem[]): BoardItem[] {
    return [...items].sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
  }

  private parseTimestamp(value: unknown): Date {
    if (!value) {
      return new Date();
    }
    if (value instanceof Date) {
      return value;
    }
    const maybe = value as { toDate?: () => Date };
    if (typeof maybe.toDate === 'function') {
      return maybe.toDate();
    }
    return new Date();
  }
}
